import { zoneScene, type DesignLayer } from '@pz/scene';
import {
  el,
  iconBtn,
  iconSvg,
  layerThumbnail,
  layerTitle,
  note,
  type DesignerCtx,
} from './panels.js';
import { round } from './state.js';
import { canDelete, layerLabel, run } from './commands.js';

/**
 * Slojevi: the stack, top of the list first.
 *
 * `answer.layers` IS the paint order (first at the bottom), so this list is
 * that array read backwards. Reordering is a pointer drag on the grip — not
 * HTML5 drag-and-drop, which does not exist on touch — with arrow keys on the
 * same grip for anyone who cannot drag at all.
 *
 * The eye controls persistent print visibility through the shared authoring
 * command. Hidden content stays editable in the layer list and survives reuse.
 */

const THUMB_WIDTH = 260;

export function renderLayersPanel(body: HTMLElement, ctx: DesignerCtx): () => void {
  const state = ctx.store.state();
  const layers = state.answer.layers;
  const cleanups: Array<() => void> = [];

  if (!layers.length) {
    body.appendChild(note(ctx.texts.nothingHere));
    return () => undefined;
  }

  // ── The whole design, once ──────────────────────────────────────────────
  const preview = el('div', 'pzd-zone-thumb');
  body.appendChild(preview);
  let cancelled = false;
  cleanups.push(() => {
    cancelled = true;
  });
  void paintZoneThumb(ctx, preview, () => cancelled);

  // ── The stack ───────────────────────────────────────────────────────────
  const list = el('div', 'pzd-list');
  list.setAttribute('role', 'list');
  const rows = new Map<string, HTMLElement>();
  const order = [...layers].reverse();

  for (const layer of order) {
    const row = buildRow(ctx, layer, state.selectedId === layer.id);
    rows.set(layer.id, row);
    list.appendChild(row);
  }
  body.appendChild(list);
  // The reference editor ends the list with a Background row, because the
  // background IS the bottom of the stack even though it is not a layer.
  if (ctx.config.allow.background) body.appendChild(backgroundRow(ctx));
  body.appendChild(el('div', 'pzd-hint', ctx.texts.reorderHint));
  if (layers.some((layer) => ctx.isHidden(layer.id))) {
    body.appendChild(note(ctx.texts.hiddenNote, 'warn'));
  }

  // ── Dragging the grips ──────────────────────────────────────────────────
  let dragId: string | undefined;
  let dropAt: number | undefined;

  const rowRects = (): Array<{ id: string; top: number; bottom: number }> =>
    [...rows].map(([id, row]) => {
      const rect = row.getBoundingClientRect();
      return { id, top: rect.top, bottom: rect.bottom };
    });

  const onMove = (event: PointerEvent): void => {
    if (!dragId) return;
    const rects = rowRects();
    let visualIndex = rects.findIndex((r) => event.clientY < (r.top + r.bottom) / 2);
    if (visualIndex < 0) visualIndex = rects.length - 1;
    dropAt = visualIndex;
    rects.forEach((r, index) => {
      const row = rows.get(r.id);
      if (!row) return;
      row.classList.toggle('is-drop-before', index === visualIndex && r.id !== dragId);
      row.classList.remove('is-drop-after');
    });
  };

  const onUp = (): void => {
    if (!dragId) return;
    const id = dragId;
    dragId = undefined;
    for (const row of rows.values()) {
      row.classList.remove('is-drag', 'is-drop-before', 'is-drop-after');
    }
    if (dropAt === undefined) return;
    // The list runs top-down but `answer.layers` runs bottom-up.
    const to = Math.max(0, Math.min(layers.length - 1, layers.length - 1 - dropAt));
    dropAt = undefined;
    ctx.dispatch({ type: 'reorder', id, to });
  };

  for (const [id, row] of rows) {
    const grip = row.querySelector('.pzd-lgrip') as HTMLElement | null;
    if (!grip) continue;
    grip.addEventListener('pointerdown', (event) => {
      dragId = id;
      dropAt = undefined;
      row.classList.add('is-drag');
      try {
        grip.setPointerCapture(event.pointerId);
      } catch {
        /* the document listeners below still finish the drag */
      }
      event.preventDefault();
    });
    grip.addEventListener('pointermove', onMove);
    grip.addEventListener('pointerup', onUp);
    grip.addEventListener('pointercancel', () => {dropAt=undefined;onUp();});
    grip.addEventListener('keydown', (event) => {
      const key = (event as KeyboardEvent).key;
      if (key !== 'ArrowUp' && key !== 'ArrowDown') return;
      event.preventDefault();
      // Up the list is up the stack: towards the front of the design.
      ctx.dispatch({ type: key === 'ArrowUp' ? 'raise' : 'lower', id });
    });
  }

  return () => {
    for (const stop of cleanups) stop();
  };
}

/** The Background row: not a layer, but the bottom of the same stack. */
function backgroundRow(ctx: DesignerCtx): HTMLElement {
  const row = el('div', 'pzd-lrow is-background');
  row.setAttribute('role', 'listitem');
  const thumb = el('div', 'pzd-lthumb');
  const background = ctx.store.value().background;
  thumb.style.background = background?.hex ?? '';
  const name = el('button', 'pzd-lname');
  name.type = 'button';
  name.append(
    el('b', undefined, ctx.texts.toolBackground),
    el('i', undefined, background?.hex ?? ctx.texts.backgroundNone),
  );
  name.addEventListener('click', () => ctx.openTool('background'));
  row.append(el('span', 'pzd-lgrip is-empty'), thumb, name);
  return row;
}

function buildRow(ctx: DesignerCtx, layer: DesignLayer, selected: boolean): HTMLElement {
  const hidden = layer.visible === false;
  const row = el('div', `pzd-lrow${selected ? ' is-on' : ''}${hidden ? ' is-off' : ''}`);
  row.setAttribute('role', 'listitem');

  const grip = el('button', 'pzd-lgrip');
  grip.type = 'button';
  grip.innerHTML = iconSvg('grip', 15);
  grip.setAttribute('aria-label', ctx.texts.reorderHint);
  grip.title = ctx.texts.reorderHint;

  // A checkbox of its own, so several layers can be gathered for the
  // Poravnanje panel without hunting for Ctrl+click on the canvas.
  const pick = el('input', 'pzd-lpick');
  pick.type = 'checkbox';
  pick.checked = ctx.selection.has(layer.id);
  pick.setAttribute('aria-label', layerTitle(ctx, layer));
  pick.addEventListener('change', () => {
    if (pick.checked) ctx.selection.add(layer.id);
    else ctx.selection.delete(layer.id);
    ctx.dispatch({ type: 'select', id: pick.checked ? layer.id : [...ctx.selection].at(-1) ?? null });
    ctx.refreshPanel();
  });

  const thumb = layerThumbnail(ctx, layer);

  const name = el('button', 'pzd-lname');
  name.type = 'button';
  // A template object shows the label the shop authored ("Vaše ime") — the
  // same word the product card and the order sheet use for it.
  const strong = el('b', undefined, layerLabel(layer) ?? layerTitle(ctx, layer));
  const kind = el('i', undefined, kindWord(ctx, layer));
  name.append(strong, kind);
  name.addEventListener('click', () => {
    ctx.dispatch({ type: 'select', id: layer.id });
    ctx.openTool(layer.kind === 'text' ? 'text'
      : layer.kind === 'image' ? 'image'
        : layer.kind === 'qr' || layer.kind === 'shape' ? 'elements' : 'icons');
  });

  const eye = iconBtn(hidden ? 'eyeOff' : 'eye', hidden ? 'Prikaži u štampi' : 'Sakrij iz štampe', () => {
    run(ctx,{op:'properties',ids:[layer.id],patch:{visible:hidden}});
  }, { className: `pzd-lbtn${hidden ? '' : ' is-on'}`, size: 17 });

  const lock = iconBtn(layer.locked ? 'lock' : 'unlock', layer.locked ? ctx.texts.unlock : ctx.texts.lock, () => {
    run(ctx,{op:'properties',ids:[layer.id],patch:{locked:!layer.locked}});
  }, { className: `pzd-lbtn${layer.locked ? ' is-on' : ''}`, size: 17 });

  const remove = iconBtn('trash', ctx.texts.remove, () => run(ctx, { op: 'delete', ids: [layer.id] }), {
    className: 'pzd-lbtn',
    size: 17,
    disabled: !canDelete(layer),
  });

  row.append(grip, pick, thumb, name, eye, lock, remove);
  row.setAttribute('aria-label', `${layerTitle(ctx, layer)} — ${kindWord(ctx, layer)}`);
  return row;
}

function kindWord(ctx: DesignerCtx, layer: DesignLayer): string {
  switch (layer.kind) {
    case 'text': return ctx.texts.layerText;
    case 'image': return ctx.texts.layerImage;
    case 'artwork': return ctx.texts.layerArtwork;
    case 'shape': return ctx.texts.layerShape;
    case 'qr': return 'QR kod';
  }
}

/**
 * The design as one picture, through the same `zoneScene` the cart card uses —
 * so the shopper sees in the panel exactly what the product page will show.
 */
async function paintZoneThumb(ctx: DesignerCtx, host: HTMLElement, cancelled: () => boolean): Promise<void> {
  const raw = ctx.renderer.renderRaw;
  if (!raw) {
    host.remove();
    return;
  }
  const pxPerMm = THUMB_WIDTH / Math.max(1, ctx.config.sizeMm.w);
  const scene = zoneScene(ctx.rawConfig, ctx.store.value(), pxPerMm);
  let out: { pixels: Uint8Array; width: number; height: number } | undefined;
  try {
    out = await raw.call(ctx.renderer, scene, 1);
  } catch {
    out = undefined;
  }
  if (cancelled() || !out || out.width <= 0) {
    host.remove();
    return;
  }
  const canvas = el('canvas');
  canvas.width = out.width;
  canvas.height = out.height;
  const context = canvas.getContext('2d');
  if (!context) {
    host.remove();
    return;
  }
  const bytes = new Uint8ClampedArray(out.pixels.byteLength);
  bytes.set(out.pixels);
  context.putImageData(new ImageData(bytes, out.width, out.height), 0, 0);
  canvas.style.width = `${round(out.width, 1)}px`;
  canvas.style.background = '#fff';
  host.textContent = '';
  host.appendChild(canvas);
}
